/**
 * BondInfoModal - Add/edit a single bond power entry
 * Ported from legacy bondInfoModal.jsx
 */

import { useState, useEffect } from 'react';
import {
  getUnexecutedPowers,
} from '../../../../services/oathTransferBondService';
import {
  OathModal,
  OathButton,
  OathInput,
  OathSelect,
  OathAlert,
  OathSpinner,
} from '../../../shared/OathUI';
import { formatCurrencyInput, unformatCurrencyInput } from '../shared/formatUtils';

const BondInfoModal = ({ isOpen, onClose, onSave, bondInfo, agencyId, transactionId, alreadyUsedPowerIds = [] }) => {
  // Track both powerId (for dropdown selection) and powerNumber (for API payload)
  const [selectedPowerId, setSelectedPowerId] = useState('');
  const [selectedPowerNumber, setSelectedPowerNumber] = useState('');
  const [bondAmount, setBondAmount] = useState('');
  const [caseNumber, setCaseNumber] = useState('');
  const [executionDate, setExecutionDate] = useState('');
  const [courtDate, setCourtDate] = useState('');
  const [offenseType, setOffenseType] = useState('');
  const [offense, setOffense] = useState('');
  const [error, setError] = useState('');

  const [availablePowers, setAvailablePowers] = useState([]);
  const [rawPowers, setRawPowers] = useState([]); // Full power objects for lookup
  const [loadingPowers, setLoadingPowers] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const isEditing = !!bondInfo?.powerId;

  // Fetch unexecuted powers for dropdown
  useEffect(() => {
    if (!isOpen || !agencyId) return;

    const fetchPowers = async () => {
      setLoadingPowers(true);
      const response = await getUnexecutedPowers(agencyId);
      if (response.success) {
        const data = response.data || [];

        // Filter out already-used powers (except the one being edited)
        const editingPowerId = bondInfo?.powerId;
        let filtered = data.filter(
          (p) => !alreadyUsedPowerIds.includes(p.powerId) || p.powerId === editingPowerId
        );

        // If editing and the current power isn't in the list, add it back
        // (matches legacy behavior lines 72-77)
        if (bondInfo && bondInfo.powerId) {
          const powerExists = filtered.some((p) => p.powerId === bondInfo.powerId);
          if (!powerExists) {
            filtered.push({
              powerId: bondInfo.powerId,
              powerNumber: bondInfo.powerNumber,
            });
          }
        }

        setRawPowers(filtered);
        setAvailablePowers(
          filtered.map((p) => ({
            value: p.powerId?.toString() || '',
            label: p.powerNumber || '',
          }))
        );
      }
      setLoadingPowers(false);
    };

    fetchPowers();
  }, [isOpen, agencyId, alreadyUsedPowerIds, bondInfo]);

  // Populate form when editing
  useEffect(() => {
    if (bondInfo && isOpen) {
      setSelectedPowerId(bondInfo.powerId?.toString() || '');
      setSelectedPowerNumber(bondInfo.powerNumber || '');
      setBondAmount(bondInfo.bondAmount?.toString() || '');
      setCaseNumber(bondInfo.caseNumber || '');
      setExecutionDate(bondInfo.executionDate ? bondInfo.executionDate.split('T')[0] : '');
      setCourtDate(bondInfo.courtDate ? bondInfo.courtDate.split('T')[0] : '');
      setOffenseType(bondInfo.offenseType || '');
      setOffense(bondInfo.offense || '');
    } else if (isOpen) {
      setSelectedPowerId('');
      setSelectedPowerNumber('');
      setBondAmount('');
      setCaseNumber('');
      setExecutionDate('');
      setCourtDate('');
      setOffenseType('');
      setOffense('');
    }
  }, [bondInfo, isOpen]);

  // When power selection changes, look up the powerNumber string
  const handlePowerChange = (value) => {
    setSelectedPowerId(value);
    const match = rawPowers.find((p) => p.powerId?.toString() === value);
    setSelectedPowerNumber(match?.powerNumber || '');
  };

  const validate = () => {
    if (!selectedPowerId) {
      setError('Power Number, Bond Amount, and Execution Date are required.');
      return false;
    }
    if (!bondAmount || parseFloat(bondAmount) <= 0) {
      setError('Power Number, Bond Amount, and Execution Date are required.');
      return false;
    }
    if (!executionDate) {
      setError('Power Number, Bond Amount, and Execution Date are required.');
      return false;
    }
    // Execution date cannot be in the future
    const today = new Date().toISOString().split('T')[0];
    if (executionDate > today) {
      setError('Execution Date cannot be in the future.');
      return false;
    }
    setError('');
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;

    setIsSaving(true);
    try {
      // Payload matches legacy format: PowerNumber (string), not powerId
      await onSave({
        transferBondId: bondInfo?.transferBondId || null,
        powerNumber: selectedPowerNumber,
        bondAmount: parseFloat(bondAmount),
        caseNumber: caseNumber.trim() || null,
        executionDate: executionDate ? new Date(executionDate) : null,
        courtDate: courtDate ? new Date(courtDate) : null,
        offense: offense.trim() || null,
        offenseType: offenseType.trim() || null,
      });
      handleClose();
    } catch {
      setError('Failed to save bond information.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setError('');
    setSelectedPowerId('');
    setSelectedPowerNumber('');
    setBondAmount('');
    setCaseNumber('');
    setExecutionDate('');
    setCourtDate('');
    setOffenseType('');
    setOffense('');
    onClose();
  };

  return (
    <OathModal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditing ? 'Edit Bond Information' : 'Add Bond Information'}
      size="md"
      footer={
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto sm:ml-auto">
          <OathButton
            variant="outline"
            onClick={handleClose}
            disabled={isSaving}
            className="w-full sm:w-auto"
          >
            Cancel
          </OathButton>
          <OathButton
            onClick={handleSave}
            loading={isSaving}
            disabled={loadingPowers}
            className="w-full sm:w-auto"
          >
            {isSaving ? 'Saving...' : isEditing ? 'Update' : 'Add'}
          </OathButton>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <OathAlert variant="error" message={error} />}

        {loadingPowers ? (
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <OathSpinner size="sm" /> Loading available powers...
          </div>
        ) : (
          <OathSelect
            label="Power"
            value={selectedPowerId}
            onChange={handlePowerChange}
            options={availablePowers}
            placeholder="Select a power..."
            required
          />
        )}

        <OathInput
          label="Bond Amount"
          value={formatCurrencyInput(bondAmount)}
          onChange={(val) => setBondAmount(unformatCurrencyInput(val).replace(/[^0-9.]/g, ''))}
          placeholder="0.00"
          prefix="$"
          required
        />

        <OathInput
          label="Case Number"
          value={caseNumber}
          onChange={setCaseNumber}
          placeholder="Case #"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <OathInput
            label="Execution Date"
            type="date"
            value={executionDate}
            onChange={setExecutionDate}
            required
          />
          <OathInput
            label="Court Date"
            type="date"
            value={courtDate}
            onChange={setCourtDate}
          />
        </div>

        <OathInput
          label="Offense Type"
          value={offenseType}
          onChange={setOffenseType}
          placeholder="Offense type"
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Offense
          </label>
          <textarea
            value={offense}
            onChange={(e) => setOffense(e.target.value.substring(0, 250))}
            placeholder="Offense description"
            maxLength={250}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-xl text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
            rows={3}
          />
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{offense.length}/250</p>
        </div>
      </div>
    </OathModal>
  );
};

export default BondInfoModal;
